/**
 * Pagina Pagamenti Buyer - Visualizza ordini da pagare e storico pagamenti
 */
import React, { useEffect, useState } from 'react';
import { formatCurrency } from '@/lib/utils';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Wallet,
  CreditCard,
  Building2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Euro,
  Loader2,
  Receipt,
  ArrowRight,
  ShoppingCart,
  Calendar,
  Eye,
  Banknote,
  History,
  TrendingUp,
  FileText,
  Download
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Order {
  id: string;
  order_number: string;
  seller_id: string;
  seller_name?: string;
  total: number;
  status: string;
  payment_status: string;
  created_at: string;
  paid_at?: string;
}

interface SellerCredit {
  seller_id: string;
  seller_name: string;
  has_credit_system: boolean;
  credit_enabled: boolean;
  credit_limit: number;
  current_balance: number;
  available_credit: number;
  percentage_used: number;
}

interface Invoice {
  id: string;
  invoice_number: string;
  total_amount: number;
  invoice_date: string;
  pdf_path?: string;
  pdf_filename?: string;
  seller_data?: { name?: string };
  buyer_data?: { name?: string };
  created_at: string;
}

const BuyerPaymentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [unpaidOrders, setUnpaidOrders] = useState<Order[]>([]);
  const [paidOrders, setPaidOrders] = useState<Order[]>([]);
  const [sellerCredits, setSellerCredits] = useState<SellerCredit[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [paying, setPaying] = useState(false);
  const [activeTab, setActiveTab] = useState('da-pagare');
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  // Verifica stato pagamento al ritorno da Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      checkPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Pagamento annullato');
      // Pulisci i parametri URL
      setSearchParams({});
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId: string) => {
    setCheckingPayment(true);
    try {
      const res = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        toast.success('Pagamento completato con successo!');
        setActiveTab('pagati');
        // Ricarica i dati
        await fetchData();
      } else {
        toast.info('Pagamento in elaborazione...');
      }
      
      // Pulisci i parametri URL
      setSearchParams({});
    } catch (error: any) {
      console.error('Error checking payment status:', error);
      toast.error('Errore nella verifica del pagamento');
    } finally {
      setCheckingPayment(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'buyer') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      // Ottieni tutti gli ordini
      const ordersRes = await axios.get(`${API}/orders`);
      // L'API restituisce direttamente un array, non un oggetto con chiave 'orders'
      const allOrders = Array.isArray(ordersRes.data) ? ordersRes.data : (ordersRes.data.orders || []);
      
      // Separa ordini pagati e non pagati
      const unpaid = allOrders.filter((o: Order) => 
        o.payment_status !== 'paid' && o.status !== 'draft' && o.status !== 'cancelled'
      );
      const paid = allOrders.filter((o: Order) => o.payment_status === 'paid');
      
      setUnpaidOrders(unpaid);
      setPaidOrders(paid);
      
      // Ottieni crediti per ogni seller
      const relRes = await axios.get(`${API}/relationships`);
      const relationships = relRes.data.relationships || [];
      
      const creditsData: SellerCredit[] = [];
      for (const rel of relationships.filter((r: any) => r.status === 'active')) {
        try {
          const creditRes = await axios.get(`${API}/billing/my-credit/${rel.seller_id}`);
          if (creditRes.data.has_credit_system) {
            creditsData.push({
              seller_id: rel.seller_id,
              seller_name: rel.partner_company?.name || 'Fornitore',
              ...creditRes.data
            });
          }
        } catch (err) {
          // Ignora errori
        }
      }
      setSellerCredits(creditsData);
      
      // Carica le fatture
      try {
        const invoicesRes = await axios.get(`${API}/billing/invoices`);
        setInvoices(invoicesRes.data.invoices || []);
      } catch (err) {
        console.error('Error fetching invoices:', err);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === unpaidOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(unpaidOrders.map(o => o.id));
    }
  };

  const getSelectedTotal = () => {
    return unpaidOrders
      .filter(o => selectedOrders.includes(o.id))
      .reduce((sum, o) => sum + (o.total || 0), 0);
  };

  const handleDownloadInvoice = async (invoiceId: string, filename: string) => {
    try {
      const response = await axios.get(`${API}/billing/invoices/${invoiceId}/download`, {
        responseType: 'blob'
      });
      
      // Crea un link per il download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `fattura_${invoiceId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Fattura scaricata');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Errore nel download della fattura');
    }
  };

  const handlePaySelected = () => {
    if (selectedOrders.length === 0) {
      toast.error('Seleziona almeno un ordine da pagare');
      return;
    }
    setShowPayDialog(true);
  };

  const handleConfirmPayment = async () => {
    setPaying(true);
    try {
      const originUrl = window.location.origin;
      
      const res = await axios.post(`${API}/billing/checkout/create`, {
        order_ids: selectedOrders,
        origin_url: originUrl
      });
      
      // Redirect a Stripe Checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('URL checkout non ricevuto');
      }
      
    } catch (error: any) {
      // Gestione errori - estrai il messaggio in modo sicuro
      let errorMessage = 'Errore nella creazione del pagamento';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Errore di validazione Pydantic
          errorMessage = detail.map((e: any) => e.msg || String(e)).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      setPaying(false);
      setShowPayDialog(false);
    }
  };

  // Solo per buyer
  if (user?.role !== 'buyer') {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Questa pagina è riservata ai buyer.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading || checkingPayment) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        {checkingPayment && (
          <p className="text-muted-foreground">Verifica pagamento in corso...</p>
        )}
      </div>
    );
  }

  // Calcola totali
  const totalUnpaid = unpaidOrders.reduce((sum, o) => sum + (o.total || 0), 0);
  const totalPaid = paidOrders.reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wallet className="h-7 w-7" />
          I Miei Pagamenti
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestisci i tuoi pagamenti e visualizza lo storico
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={totalUnpaid > 0 ? 'border-amber-200 bg-amber-50 dark:bg-amber-900/20' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${totalUnpaid > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-muted'}`}>
                <Clock className={`h-6 w-6 ${totalUnpaid > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Da Pagare</p>
                <p className={`text-2xl font-bold ${totalUnpaid > 0 ? 'text-amber-700 dark:text-amber-300' : ''}`}>
                  {formatCurrency(totalUnpaid)}
                </p>
                <p className="text-xs text-muted-foreground">{unpaidOrders.length} ordini</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pagati</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalPaid)}</p>
                <p className="text-xs text-muted-foreground">{paidOrders.length} ordini</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Credito Disponibile</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(
                    sellerCredits.reduce((sum, c) => sum + (c.available_credit || 0), 0)
                  )}
                </p>
                <p className="text-xs text-muted-foreground">{sellerCredits.filter(c => c.credit_enabled).length} fornitori</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Crediti per Fornitore */}
      {sellerCredits.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Banknote className="h-5 w-5" />
              Credito per Fornitore
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {sellerCredits.map((credit) => (
                <div key={credit.seller_id} className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{credit.seller_name}</span>
                    </div>
                    {credit.credit_enabled ? (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        Credito Attivo
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Pagamento Immediato</Badge>
                    )}
                  </div>
                  {credit.credit_enabled && (
                    <>
                      <Progress 
                        value={credit.percentage_used} 
                        className={`h-2 mb-2 ${credit.percentage_used >= 80 ? '[&>div]:bg-amber-500' : ''} ${credit.percentage_used >= 100 ? '[&>div]:bg-red-500' : ''}`}
                      />
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Usato: {formatCurrency(credit.current_balance)}</span>
                        <span>Limite: {formatCurrency(credit.credit_limit)}</span>
                        <span className={credit.available_credit <= 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                          Disponibile: {formatCurrency(credit.available_credit)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs Ordini */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="da-pagare" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Da Pagare ({unpaidOrders.length})
          </TabsTrigger>
          <TabsTrigger value="pagati" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Pagati ({paidOrders.length})
          </TabsTrigger>
          <TabsTrigger value="fatture" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Fatture ({invoices.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab: Da Pagare */}
        <TabsContent value="da-pagare">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Ordini da Pagare</CardTitle>
                  <CardDescription>Seleziona gli ordini che vuoi pagare</CardDescription>
                </div>
                {unpaidOrders.length > 0 && (
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={handleSelectAll}>
                      {selectedOrders.length === unpaidOrders.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                    </Button>
                    <Button 
                      onClick={handlePaySelected}
                      disabled={selectedOrders.length === 0}
                      className="gap-2"
                    >
                      <CreditCard className="h-4 w-4" />
                      Paga Selezionati ({formatCurrency(getSelectedTotal())})
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {unpaidOrders.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <p className="text-lg font-medium text-green-600">Tutto in regola!</p>
                  <p className="text-muted-foreground">Non hai ordini da pagare</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <input 
                          type="checkbox"
                          checked={selectedOrders.length === unpaidOrders.length}
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      </TableHead>
                      <TableHead>Ordine</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unpaidOrders.map((order) => (
                      <TableRow key={order.id} className="cursor-pointer hover:bg-muted/50">
                        <TableCell>
                          <input 
                            type="checkbox"
                            checked={selectedOrders.includes(order.id)}
                            onChange={() => handleSelectOrder(order.id)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        </TableCell>
                        <TableCell>
                          <span className="font-mono font-medium">
                            #{order.order_number || order.id.slice(0,8)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            {order.seller_name || 'Fornitore'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            {new Date(order.created_at).toLocaleDateString('it-IT')}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                            {order.status === 'submitted' ? 'Inviato' : 
                             order.status === 'confirmed' ? 'Confermato' : 
                             order.status === 'shipped' ? 'Spedito' : 
                             order.status === 'delivered' ? 'Consegnato' : order.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(order.total || 0)}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/orders/${order.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Pagati */}
        <TabsContent value="pagati">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Storico Pagamenti
              </CardTitle>
              <CardDescription>Ordini già saldati</CardDescription>
            </CardHeader>
            <CardContent>
              {paidOrders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun pagamento effettuato</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ordine</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead>Data Ordine</TableHead>
                      <TableHead>Data Pagamento</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paidOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>
                          <span className="font-mono font-medium">
                            #{order.order_number || order.id.slice(0,8)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            {order.seller_name || 'Fornitore'}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell>
                          {order.paid_at ? (
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              {new Date(order.paid_at).toLocaleDateString('it-IT')}
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-bold text-green-600">
                          {formatCurrency(order.total || 0)}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/orders/${order.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Fatture */}
        <TabsContent value="fatture">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Le Mie Fatture
              </CardTitle>
              <CardDescription>Fatture ricevute dai tuoi fornitori</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna fattura disponibile</p>
                  <p className="text-sm mt-1">Le fatture verranno generate automaticamente dopo ogni pagamento</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Numero</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead className="text-center">Download</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <span className="font-mono font-medium text-primary">
                            {invoice.invoice_number}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            {invoice.seller_data?.name || 'Fornitore'}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {invoice.invoice_date || new Date(invoice.created_at).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(invoice.total_amount || 0)}
                        </TableCell>
                        <TableCell className="text-center">
                          {invoice.pdf_path ? (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDownloadInvoice(invoice.id, invoice.pdf_filename || `fattura_${invoice.invoice_number}.pdf`)}
                              className="gap-2"
                            >
                              <Download className="h-4 w-4" />
                              PDF
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">Non disponibile</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pay Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Conferma Pagamento
            </DialogTitle>
            <DialogDescription>
              Stai per pagare {selectedOrders.length} ordine/i
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted/30 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-muted-foreground">Ordini selezionati</span>
                <span className="font-medium">{selectedOrders.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Totale da pagare</span>
                <span className="text-2xl font-bold">{formatCurrency(getSelectedTotal())}</span>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Metodo di pagamento</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="h-16 flex-col gap-1">
                  <CreditCard className="h-5 w-5" />
                  <span className="text-xs">Carta di Credito</span>
                </Button>
                <Button variant="outline" className="h-16 flex-col gap-1">
                  <Wallet className="h-5 w-5" />
                  <span className="text-xs">PayPal</span>
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleConfirmPayment}
              disabled={paying}
              className="gap-2"
            >
              {paying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Elaborazione...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4" />
                  Paga {formatCurrency(getSelectedTotal())}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuyerPaymentsPage;
